import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import { IconClose } from './Icons';
import './phases.css';

const nf = (n) => (n || 0).toLocaleString('it-IT');

const MagIcon = ({ size = 30, dim }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={dim ? { opacity: .55 } : undefined}>
    <circle cx="10" cy="10" r="7.6" /><path d="M21.5 21.5 15.4 15.4" />
    <path d="M10 5.9c-1.85 0-3.25 1.35-3.25 3.1 0 2.1 3.25 5.1 3.25 5.1s3.25-3 3.25-5.1C13.25 7.25 11.85 5.9 10 5.9z" />
    <circle cx="10" cy="9.1" r="1.15" fill="#f97316" stroke="none" />
  </svg>
);

/* Dialog dell'agente Indaga (pool di sistema): animazione + statistiche + velocità.
   Gemello di VfStatsDialog, con i dati di /indaga/stato. */
export default function IndagaStatsDialog({ open, onClose }) {
  const [info, setInfo] = useState(null);
  const [closing, setClosing] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [speed, setSpeed] = useState(null);
  const sample = useRef(null);

  const handleClose = () => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => { setClosing(false); onClose?.(); }, 320);
  };

  useEffect(() => {
    if (!open) return;
    setClosing(false); setConfirmStop(false); setSpeed(null); sample.current = null;
    const load = () => axios.get(`${API}/indaga/stato`).then((r) => {
      setInfo(r.data);
      const j = r.data?.job;
      if (j && j.status === 'running') {
        const now = Date.now();
        const prev = sample.current;
        if (prev && j.completed > prev.c) {
          const dm = (now - prev.t) / 60000;
          if (dm > 0) setSpeed(Math.round((j.completed - prev.c) / dm));
        }
        sample.current = { c: j.completed, t: now };
      } else { sample.current = null; setSpeed(null); }
    }).catch(() => {});
    load();
    const id = setInterval(load, 1500);
    return () => clearInterval(id);
  }, [open]);

  const ferma = async () => {
    setStopping(true);
    try { await axios.post(`${API}/indaga/agente`, { attivo: false }); toast.info('AI Indaga fermato'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    setStopping(false); setConfirmStop(false);
  };

  if (!open) return null;

  const job = info?.job;
  const running = job && job.status === 'running';
  const inAttesa = info?.in_attesa;
  const pct = job && job.total ? Math.round((100 * job.completed) / job.total) : 0;

  return createPortal(
    <div className={`bulk-overlay${closing ? ' closing' : ''}`}>
      <div className="bulk-dialog">
        <div className="bulk-bg">
          <div className="bulk-grid" />
          {running && (
            <div className="bulk-radar">
              <span className="bulk-ring r0" /><span className="bulk-ring r1" />
              <span className="bulk-ring r2" /><span className="bulk-ring r3" />
            </div>
          )}
        </div>

        <div className="bulk-content">
          <div className="bulk-head">
            <span className="bulk-title">AI Indaga · reverse lookup</span>
            <button className="bulk-x" onClick={handleClose}><IconClose size={16} color="#fff" /></button>
          </div>

          {!running ? (
            <div className="bulk-empty">
              <div className="bulk-empty-ico"><MagIcon size={30} dim /></div>
              <p>{inAttesa ? 'AI Indaga in attesa di record' : 'AI Indaga fermo'}</p>
              {info?.motivo_attesa && <p style={{ fontSize: 11, opacity: .6 }}>{info.motivo_attesa}</p>}
              {info?.eligibili != null && (
                <p style={{ fontSize: 12, opacity: .7 }}>Da indagare: <b>{nf(info.eligibili)}</b></p>
              )}
            </div>
          ) : (
            <div className="ph-wrap">
              <div className="ph-label">Indagine numeri</div>
              <div className="ph-sub">
                {job.current ? <>indago: <span className="ph-num">{job.current}</span></> : 'preparazione…'}
              </div>

              <div className="ph-stats">
                <div className="ph-stats-head">
                  <span>{nf(job.completed)}/{nf(job.total)} ({pct}%)</span>
                  {speed != null && <span style={{ opacity: .85 }}>{nf(speed)} num/min</span>}
                </div>
                <div className="ph-stack">
                  <span style={{ width: `${pct}%`, background: '#f97316' }} />
                </div>
                <div className="ph-legend" style={{ flexWrap: 'wrap' }}>
                  <span><i style={{ background: '#22c55e' }} />Completati {nf(job.completati)}</span>
                  <span><i style={{ background: '#f59e0b' }} />Non conf. {nf(job.non_confermati)}</span>
                  <span><i style={{ background: '#94a3b8' }} />Incompleti {nf(job.incompleti)}</span>
                  <span><i style={{ background: '#64748b' }} />Non trovati {nf(job.non_trovati)}</span>
                  <span><i style={{ background: '#ef4444' }} />Errori {nf(job.errori)}</span>
                </div>
              </div>

              {confirmStop ? (
                <div className="bulk-confirm">
                  <span className="bulk-confirm-q">Fermare l'agente Indaga?</span>
                  <div className="bulk-confirm-btns">
                    <button className="bulk-cancel" onClick={ferma} disabled={stopping}>{stopping ? '…' : 'Sì'}</button>
                    <button className="bulk-keep" onClick={() => setConfirmStop(false)} disabled={stopping}>No</button>
                  </div>
                </div>
              ) : (
                <button className="bulk-cancel" onClick={() => setConfirmStop(true)}>Ferma agente</button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
